import {
  CHART_DISPLAY_TYPES,
  ChartDisplayType,
  NUMERIC_AGGREGATIONS,
  NumericAggregation,
  Period,
  PERIODS,
} from "@/shared/domain/metrics";

/**
 * The persisted `dashboard_definition` schema: the types stored in a
 * `DashboardRecord`'s JSONB, the enums those types range over, and the
 * structural validator (`isDashboardDefinition`) that gates anything loaded
 * from outside the running app. Feature-level layout/URL logic lives in
 * `@/MetricsDashboard/components/model/dashboardParams`; this module is
 * deliberately leaf-like (it imports only from the shared metrics domain) so
 * shared code never depends on a feature.
 */

/** The dashboard_definition schema version. */
export const DASHBOARD_SCHEMA_VERSION = 1;

/**
 * The time windows a dashboard can aggregate over. Part of the persisted
 * schema: a stored definition's `range` must be one of these values.
 */
export const TIME_RANGES = [
  { value: "30d", label: "Last 30 days", days: 30 },
  { value: "90d", label: "Last 90 days", days: 90 },
  { value: "365d", label: "Last year", days: 365 },
  { value: "all", label: "All time", days: 3650 },
] as const;

export type TimeRange = (typeof TIME_RANGES)[number]["value"];

/**
 * The persisted `dashboard_definition` shape. Anything else (corrupt storage,
 * a hand-edited record) is simply invalid: loaders gate on
 * {@link isDashboardDefinition} and fall back to their invalid-input path.
 */
export interface DashboardDefinition {
  schema_version: typeof DASHBOARD_SCHEMA_VERSION;
  range: TimeRange;
  period: Period;
  sections: SectionConfig[];
  cards: CardConfig[];
}

export type CardKind = "trend" | "chart";

/**
 * A card's explicit cell placement on its section's master canvas: a 1-based
 * cell coordinate (`row`, `col`) and a size in cells (`w`×`h`), ALWAYS in
 * master-canvas coordinates (`MASTER_COLS` columns wide — see
 * `placedLayout.ts`). Narrower screens render a compressed projection of the
 * master layout; they never store their own coordinate space. Cards without a
 * placement flow automatically after the placed ones.
 */
export interface Placement {
  row: number;
  col: number;
  w: number;
  h: number;
}

/** A dashboard section: a titled sub-canvas, arranged horizontally by index. */
export interface SectionConfig {
  id: string;
  title: string;
}

/** Fields shared by every card kind: the section it belongs to (missing means
 *  the first section) and its explicit placement (missing means auto-flow). */
export interface PlacedCardBase {
  placement?: Placement;
  section?: string;
}

/** A single-metric KPI card (a 1×1 "trend tile"). */
export interface TrendCardConfig extends PlacedCardBase {
  id: string;
  kind: "trend";
  /** Null means "fall back to an available metric definition". */
  metric: string | null;
  aggregation: NumericAggregation;
}

export interface ChartCardConfig extends PlacedCardBase {
  id: string;
  kind: "chart";
  /** Null means "fall back to the first available metric definition". */
  metric: string | null;
  aggregation: NumericAggregation;
  display: ChartDisplayType;
}

export type CardConfig = TrendCardConfig | ChartCardConfig;

export function asTimeRange(value: string | null): TimeRange | null {
  return value !== null && TIME_RANGES.some((t) => t.value === value)
    ? (value as TimeRange)
    : null;
}

export function asPeriod(value: string | null): Period | null {
  return value !== null && (PERIODS as readonly string[]).includes(value)
    ? (value as Period)
    : null;
}

function asAggregation(value: string | null): NumericAggregation | null {
  return value !== null &&
    (NUMERIC_AGGREGATIONS as readonly string[]).includes(value)
    ? (value as NumericAggregation)
    : null;
}

/**
 * Placement cells (and spans) must be sane positive integers. The cap guards
 * the layout-packing loops in `placedLayout.ts`, which iterate over every
 * occupied cell — a definition is rejected outright rather than accepted
 * with a span that could hang the packer.
 */
const MAX_PLACEMENT_CELLS = 1000;

function isRecordValue(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isPlacementCell(value: unknown): boolean {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= MAX_PLACEMENT_CELLS
  );
}

function isPlacement(value: unknown): value is Placement {
  return (
    isRecordValue(value) &&
    isPlacementCell(value.row) &&
    isPlacementCell(value.col) &&
    isPlacementCell(value.w) &&
    isPlacementCell(value.h)
  );
}

function isCardConfig(value: unknown): value is CardConfig {
  if (
    !isRecordValue(value) ||
    typeof value.id !== "string" ||
    (value.metric !== null && typeof value.metric !== "string") ||
    asAggregation(
      typeof value.aggregation === "string" ? value.aggregation : null,
    ) === null ||
    (value.placement !== undefined && !isPlacement(value.placement)) ||
    (value.section !== undefined && typeof value.section !== "string")
  ) {
    return false;
  }
  if (value.kind === "trend") {
    return true;
  }
  return (
    value.kind === "chart" &&
    CHART_DISPLAY_TYPES.some((d) => d.value === value.display)
  );
}

function isSectionConfig(value: unknown): value is SectionConfig {
  return (
    isRecordValue(value) &&
    typeof value.id === "string" &&
    typeof value.title === "string"
  );
}

/**
 * Strict structural check for a {@link DashboardDefinition} loaded from
 * outside the running app (a backend record's JSONB, a localStorage draft).
 * Only the current schema shape passes — including its `range`/`period`
 * enums, card kinds, and placement bounds — so accepted input can never
 * reach a code path that breaks after acceptance, and rejected input takes
 * the caller's invalid-input fallback instead of loading half-broken (e.g.
 * an unknown range that would read back as a permanently-dirty dashboard).
 */
export function isDashboardDefinition(
  value: unknown,
): value is DashboardDefinition {
  return (
    isRecordValue(value) &&
    value.schema_version === DASHBOARD_SCHEMA_VERSION &&
    typeof value.range === "string" &&
    asTimeRange(value.range) !== null &&
    typeof value.period === "string" &&
    asPeriod(value.period) !== null &&
    Array.isArray(value.sections) &&
    value.sections.every(isSectionConfig) &&
    Array.isArray(value.cards) &&
    value.cards.every(isCardConfig)
  );
}
